import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Results from './Results';
import Question from '../question/Question';
import { fStopwatch } from '../../../util/format-time';
import { mislecAPI } from '../../../../util/axios';
import Iconify from '../../../../theme/iconify/iconify';
import ConfirmationModal from '../../../util/ConfirmationModal';
import { useResponsive } from '../../../../hooks/use-responsive';

function Exam({ user, updateUser, quiz, courseId, sectionId, subsectionId }) {
    const [questions, setQuestions] = React.useState(user.role === 'student' ? [] : quiz?.questions);
    const [results, setResults] = React.useState(null);
    const [submitExamModal, setSubmitExamModal] = React.useState(false);

    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');

    const submitExam = () => {
        //  Data preparation
        const data = {
            course_id: courseId,
            section_id: sectionId,
            quiz: {
                id: subsectionId,
                questions: questions.map((question) => ({
                    id: question.id,
                    answers: user.courses?.find((c) => c.id === courseId)
                        ?.quiz.find((q) => q.id === subsectionId)
                        ?.questions.find((q) => q.id === question.id)
                        ?.answers.map((a) => ({ id: a.id })) || []
                }))
            }
        };

        //  Send data to the server
        mislecAPI.put(`/api/users/${user._id}/exam/end`, data)
            .then((res) => {
                updateUser(res.data.user);
                setSubmitExamModal(false);
                fetchResults();
            })
            .catch((error) => {
                //  Expired session
                if (error?.response)
                    if (error?.response?.data?.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const initialiseTimer = () => {
        const course = user.courses?.find((c) => c.id === courseId);
        const quizData = course?.quiz?.find((q) => q.id === subsectionId);

        if (!quizData?.is_solving) return null;

        const now = new Date();
        const start = new Date(quizData.start_time);
        const end = new Date(start.getTime() + quiz?.time_limit * 60000);
        const diffMilliseconds = end - now;
        return Math.max(Math.floor(diffMilliseconds / 1000), 0);
    }
    const [timer, setTimer] = React.useState(initialiseTimer());

    React.useEffect(() => {
        if (!user.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving) return;
        if (timer <= 0) submitExam();
        let intervalId;
        intervalId = setInterval(() => {
            setTimer(prev => {
                if (prev <= 0) {
                    clearInterval(intervalId);
                    submitExam();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [timer]);

    React.useEffect(() => {
        setTimer(initialiseTimer());
    }, [user.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving]);

    React.useEffect(() => {
        if (user.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving && questions.length === 0) {
            //  Data preparation
            const data = {
                course_id: courseId,
                section_id: sectionId,
                quiz_id: subsectionId
            };

            //  Send data to the server
            mislecAPI.post(`/api/users/${user._id}/exam/randomized`, data)
                .then((res) => {
                    setQuestions(res.data.exam);
                })
                .catch((error) => {
                    //  Expired session
                    if (error?.response)
                        if (error?.response?.data?.detail === 'api.auth.verify.401')
                            updateUser(null);
                });
        }
    }, [questions]);

    const startExam = () => {
        //  Data preparation
        const data = {
            course_id: courseId,
            section_id: sectionId,
            quiz: {
                id: subsectionId,
                questions: []
            }
        };

        //  Send data to the server
        mislecAPI.put(`/api/users/${user._id}/exam/start`, data)
            .then((res) => {
                updateUser(res.data.user);
                setQuestions(res.data.exam);
            })
            .catch((error) => {
                //  Expired session
                if (error?.response)
                    if (error?.response?.data?.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const saveQuiz = (question, answers, explanations) => {
        if (!explanations) return;

        //  Data  preparation
        const data = {
            course_id: courseId,
            section_id: sectionId,
            quiz: {
                id: subsectionId,
                questions: [{
                    id: question.id,
                    answers: question.answers
                        .map((a, index) => ({ id: a.id, explanation: explanations[index]?.explanation ? explanations[index].explanation : null }))
                        .filter((a) => answers.includes(a.id))
                }]
            }
        };

        //  Send data to the server
        mislecAPI.put(`/api/users/${user._id}/quiz`, data)
            .then((res) => updateUser(res.data.user))
            .catch((error) => {
                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const fetchResults = () => {
        if (!user) return;
        if (!subsectionId) return;

        //  Fetch data from the server
        mislecAPI.get(`/api/users/${user._id}/exam/${subsectionId}/past_attempts`)
            .then((res) => {
                setResults(res.data.past_attempts.sort((a, b) => new Date(b.quiz.end_time) - new Date(a.quiz.end_time)));
            })
            .catch((error) => {
                //  Expired session
                if (error?.response)
                    if (error?.response?.data?.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    React.useEffect(() => {
        if (results) return;
        fetchResults();
    }, [results]);

    return (
        <>
            <Stack spacing={2} sx={{ mt: 2, mb: 1, zIndex: 1 }}>
                {user.role === 'student' &&
                    !user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving &&
                    ((user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.attempt ?? 0) < quiz.no_attempts) &&
                    <>
                        <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: mdUp ? '15%' : '55%', height: mdUp ? '110px' : '135px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                                    <img src={'/assets/images/personalized.png'} alt={t('home.image_alt')} style={{ width: '100%', objectFit: 'cover', objectPosition: 'center' }} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Stack spacing={1} sx={{ mt: 2 }}>
                                    <Typography variant='h6' align='center' sx={{ fontSize: '1rem !important' }}>
                                        {t('course.exam.title')}
                                    </Typography>
                                    <Typography variant='caption' align='center' sx={{ fontSize: '0.9rem !important' }}>
                                        {t('course.exam.description')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant='caption' align='center' sx={{ fontSize: '0.875rem !important', color: 'text.disabled' }}>
                                            {quiz.no_questions} {t(`course.exam.questions.${quiz.no_questions >= 5 ? 'more' : quiz.no_questions}`)}
                                        </Typography>
                                        <Divider orientation='vertical' flexItem sx={{ mx: 1, my: 0.4 }} />
                                        <Typography variant='caption' align='center' sx={{ fontSize: '0.875rem !important', color: 'text.disabled' }}>
                                            {quiz.time_limit} {t(`course.exam.minutes.${quiz.time_limit >= 5 ? 'more' : quiz.time_limit}`)}
                                        </Typography>
                                        <Divider orientation='vertical' flexItem sx={{ mx: 1, my: 0.4 }} />
                                        <Typography variant='caption' align='center' sx={{ fontSize: '0.875rem !important', color: 'text.disabled' }}>
                                            {quiz.no_attempts - (user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.attempt ?? 0)} {t(`course.exam.attempts.${(quiz.no_attempts - (user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.attempt ?? 0)) >= 5 ? 'more' : (quiz.no_attempts - (user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.attempt ?? 0))}`)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                <IconButton
                                    onClick={() => startExam()}
                                    sx={{
                                        p: '4px',
                                        pr: 0.75,
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon='tabler:stopwatch' width={20} />
                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.8rem', fontWeight: '700' }}>
                                        {t('course.exam.buttons.start')}
                                    </Typography>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </>
                }
                {(user.role !== 'student' || (user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving)) &&
                    <>
                        <Grid container>
                            <Grid item xs={12} md={!user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving ? 12 : 10}>
                                <Grid spacing={2} container>
                                    {user.role === 'student' &&
                                        questions.map((question, index) => (
                                            <Question question={question} index={index} user={user} quiz={quiz} saveQuiz={saveQuiz} courseId={courseId} subsectionId={subsectionId} />
                                        ))}
                                    {user.role === 'student' && questions.length === 0 &&
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
                                                <CircularProgress />
                                            </Box>
                                        </Grid>
                                    }
                                    {user.role !== 'student' &&
                                        quiz?.questions.map((question, index) => (
                                            <Question question={question} index={index} user={user} quiz={quiz} saveQuiz={saveQuiz} courseId={courseId} subsectionId={subsectionId} />
                                        ))}
                                </Grid>
                            </Grid>
                            {user.role === 'student' && (user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving) &&
                                <Grid
                                    item
                                    xs={12}
                                    md={2}
                                    sx={{
                                        pl: { xs: 0, md: 1 },
                                        pb: { xs: 2, md: 0 },
                                        position: 'sticky',
                                        top: '90px',
                                        alignSelf: 'flex-start',
                                        zIndex: 1000
                                    }}
                                >
                                    <Stack sx={{ border: '1px solid rgba(33, 43, 54, 0.12)', borderRadius: '8px' }}>
                                        <Grid container sx={{ backgroundColor: '#F5F6F6', borderRadius: '8px 8px 0px 0px', p: 0.5, borderBottom: '1px solid rgba(33, 43, 54, 0.12)' }}>
                                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <IconButton
                                                    sx={{
                                                        borderRadius: '8px',
                                                        p: 0.5,
                                                        px: 1,
                                                        '&:hover': {
                                                            cursor: 'default'
                                                        },
                                                    }}
                                                    disableFocusRipple
                                                    disableRipple
                                                >
                                                    <Iconify icon='tabler:stopwatch' width={20} sx={{ color: 'primary.main' }} />
                                                    <Typography variant='h6' sx={{ fontSize: '0.9rem !important', textAlign: 'center', ml: 0.5 }}>
                                                        {t('course.exam.time')}
                                                    </Typography>
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                        <Grid container flexDirection='column' justifyContent='flex-start' sx={{ p: '8px', backgroundColor: '#FFF', borderRadius: '0px 0px 16px 16px' }}>
                                            <Stack spacing={1}>
                                                <Typography variant='body1' sx={{ fontSize: '0.975rem', textAlign: 'center' }}>
                                                    {fStopwatch(timer, user.language)}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                    </Stack>
                                </Grid>
                            }
                        </Grid>
                        {user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving &&
                            <Grid container justifyContent='center'>
                                <IconButton
                                    onClick={() => setSubmitExamModal(true)}
                                    sx={{
                                        p: '4px',
                                        pr: 0.75,
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon='ph:hand-deposit-bold' width={20} />
                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.8rem', fontWeight: '700' }}>
                                        {t('course.exam.buttons.end')}
                                    </Typography>
                                </IconButton>
                            </Grid>
                        }
                    </>
                }
                {user.role === 'student' && quiz?.type === 'exam' &&
                    !user.courses.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving &&
                    ((user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.attempt ?? 0) >= quiz.no_attempts) &&
                    <>
                        <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ width: mdUp ? '12%' : '40%', height: mdUp ? '90px' : '110px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                                    <img src={'/assets/images/async.png'} alt={t('home.image_alt')} style={{ width: '100%', objectFit: 'cover', objectPosition: 'center' }} />
                                </div>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <Stack spacing={1} sx={{ mt: 2 }}>
                                    <Typography variant='h6' align='center' sx={{ fontSize: '1rem !important' }}>
                                        {t('course.exam.title')}
                                    </Typography>
                                    <Typography variant='caption' align='center' sx={{ fontSize: '0.9rem !important' }}>
                                        {t('course.exam.allAttempts')}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                }
                {user.role === 'student' &&
                    !user?.courses?.find((c) => c.id === courseId)?.quiz?.find((q) => q.id === subsectionId)?.is_solving &&
                    results && results?.length > 0 && <Results results={results} user={user} />
                }
            </Stack>
            <ConfirmationModal
                type='confirm'
                title={t('course.modals.submitExam.title')}
                description={t('course.modals.submitExam.description')}
                confirmationText={t('course.modals.submitExam.confirmation')}
                confirm={() => submitExam()}
                open={submitExamModal}
                onClose={() => setSubmitExamModal(false)}
            />
        </>
    );
}

export default Exam;